import { useState } from 'react'
import { displayTable } from '../../utils/tableLabel'
import { ChevronDownIcon, ChevronUpIcon, NoteIcon, PrinterIcon, SendToBackIcon, TableIcon } from '../icons'
import { formatTRY } from '../../utils/money'
import { formatClock } from '../../utils/dayKey'
import type { Order } from '../../types'

/**
 * How many tickets stay on screen before the rest fold away.
 *
 * A busy service pushes the menu off the bottom of a phone, and the waiter is
 * there to take an order, not to read the list of tables. Three is enough to
 * recognise the section without the catalogue being driven off screen.
 */
const VISIBLE = 3

interface Props {
  tickets: Order[]
  /** Set when this ticket is loaded into the cart for editing or settling. */
  activeId: string | null
  onPick: (ticket: Order) => void
  onCancel: (ticket: Order) => void
  /** Yoksa yazdır simgesi çıkmaz (yazıcısız kurum). */
  onPrint?: (ticket: Order) => void
  /** Waiters may add to a ticket but not scrap one. */
  canCancel: boolean
  /** Ids whose slip has come off this till's printer. */
  printedIds: Set<string>
  /** Opens the move dialog. Only shown where there is something to move. */
  onOpenMove: () => void
  /** Garsonda "Masa seç" (sipariş alan garsonda): önce masa, sonra ürünler. */
  onPickTable?: () => void
  /** Seçilmiş, henüz kaydedilmemiş masa; düğmede adı yazıyor. */
  pickedTable?: string | null
  /**
   * Whether long lists fold. On for a waiter's phone, where the tickets push
   * the menu off screen and they are there to take an order. Off at the till,
   * which is a laptop with room for the lot and whose whole job is picking a
   * ticket out of that list to settle.
   */
  foldable: boolean
}

/**
 * Tickets a waiter opened, waiting to be settled. Shown above the catalogue
 * so a cashier sees them the moment a customer walks up to pay.
 */
export function OpenTickets({
  tickets,
  activeId,
  onPick,
  onCancel,
  onPrint,
  canCancel,
  printedIds,
  onOpenMove,
  onPickTable,
  pickedTable,
  foldable,
}: Props) {
  const [expanded, setExpanded] = useState(false)
  const empty = tickets.length === 0

  const folds = foldable && tickets.length > VISIBLE
  const collapsed = folds && !expanded
  // The ticket being worked on is never folded away: a waiter mid-round would
  // otherwise watch the thing they are adding to disappear.
  const shown = collapsed
    ? tickets.filter((t, i) => i < VISIBLE || t.id === activeId)
    : tickets
  // Counted from what is actually on screen, not from the limit: keeping the
  // active ticket visible can leave one fewer folded than VISIBLE implies, and
  // a button promising three more that reveals two is worse than no button.
  const hidden = tickets.length - shown.length

  return (
    <div className="tickets">
      <div className="tickets__head">
        {/* "Masa Aç" kalktı (kullanıcı, 20 Eki 2026): sepetin "Adisyona
            Kaydet" düğmesiyle aynı pencereyi açıyordu. */}
        <h2>{empty ? 'Açık Adisyon Yok' : `Açık Adisyonlar (${tickets.length})`}</h2>
        <div className="tickets__tools">
          {onPickTable && (
            <button
              type="button"
              className={`tickets__move tickets__pick ${pickedTable ? 'is-on' : ''}`}
              onClick={onPickTable}
              title="Önce masayı seçin, sonra ürünleri ekleyin"
            >
              <TableIcon />
              {pickedTable ?? 'Masa seç'}
            </button>
          )}
          {/* Moving a ticket belongs beside the tickets, not on each card: it is
              an occasional correction and a third icon per card crowded them. */}
          {!empty && (
            <button
              type="button"
              className="tickets__move"
              onClick={onOpenMove}
              title="Adisyonu başka masaya taşı"
            >
              <SendToBackIcon />
              Taşı
            </button>
          )}
        </div>
      </div>
      <div className="tickets__list">
        {shown.map((t) => (
          <div key={t.id} className={`ticket ${t.id === activeId ? 'is-active' : ''}`}>
            <button type="button" className="ticket__main" onClick={() => onPick(t)}>
              <span className="ticket__table">
                {displayTable(t.tableName) ?? 'Masa yok'}
                {t.tableNote && (
                  <span className="ticket__note" title={t.tableNote} aria-label="Masa notu var">
                    <NoteIcon />
                  </span>
                )}
              </span>
              <span className="ticket__total">{formatTRY(t.totalKurus)}</span>
              <span className="ticket__meta">
                {t.itemCount} ürün, {formatClock(t.createdAtLocal)}
                {/* Faint on purpose: useful for sorting out a mix-up, but not
                    something the cashier needs to read on every ticket. */}
                {t.openedByName && <span className="ticket__by">, {t.openedByName}</span>}
              </span>
              {/* The ticket stays here until it is paid, a printed slip only
                  means the customer took it to the stall. Without this the
                  cashier can't tell which ones have already been printed. */}
              {printedIds.has(t.id) && (
                <span className="ticket__printed">
                  <PrinterIcon />
                  Fiş çıktı, ödeme bekliyor
                </span>
              )}
            </button>
            {/* Printing before payment: the customer takes the slip to the
                stall while the ticket stays open at the till. */}
            {onPrint && (
            <button
              type="button"
              className="ticket__side"
              onClick={() => onPrint(t)}
              title="Fiş yazdır"
              aria-label="Fiş yazdır"
            >
              <PrinterIcon />
            </button>
            )}
            {canCancel && (
              <button
                type="button"
                className="ticket__side ticket__x"
                onClick={() => onCancel(t)}
                title="Adisyonu iptal et"
                aria-label="Adisyonu iptal et"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Full width and at the foot of the list, where the tickets stop and the
          menu begins, that is where a thumb already is when the list runs out
          and the waiter wants either more of it or the catalogue below. */}
      {/* Shown whenever folding applies at all, not only while something is
          folded, once expanded, `hidden` is zero and the button would vanish
          with no way left to collapse the list again. */}
      {folds && (expanded || hidden > 0) && (
        <button
          type="button"
          className="tickets__more"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              <ChevronUpIcon />
              Adisyonları gizle
            </>
          ) : (
            <>
              <ChevronDownIcon />
              {hidden} adisyon daha
            </>
          )}
        </button>
      )}
    </div>
  )
}
